
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import BrokenImagesNotice from '@/components/helpdesk/BrokenImagesNotice.vue';
import {removeBrokenImages} from '@/services/helpdeskEmailImages';

@Component({name: 'RichTextEditor', components: {BrokenImagesNotice}})
export default class RichTextEditor extends Vue {
  @Prop({default: ''})          value!: string;
  @Prop({default: 'Escribe aquí...'}) placeholder!: string;

  focused = false;
  states = {bold: false, italic: false, underline: false};
  savedRange: Range | null = null;
  // Images the browser couldn't load (dead links): they won't reach the recipient either.
  failedSrcs: string[] = [];

  mounted() {
    const el = this.$refs.editor as HTMLElement;
    el.innerHTML = this.value || '';
    // Image load events don't bubble: listen in the capture phase.
    el.addEventListener('error', this.onMediaEvent, true);
    el.addEventListener('load', this.onMediaEvent, true);
  }

  beforeDestroy() {
    const el = this.$refs.editor as HTMLElement;
    el.removeEventListener('error', this.onMediaEvent, true);
    el.removeEventListener('load', this.onMediaEvent, true);
  }

  private onMediaEvent(event: Event) {
    const img = event.target as HTMLImageElement;
    if (!img || img.tagName !== 'IMG') return;
    const src = img.getAttribute('src') || '';
    const known = this.failedSrcs.indexOf(src);
    if (event.type === 'error' && known === -1 && src.indexOf('data:') !== 0) this.failedSrcs.push(src);
    if (event.type === 'load' && known !== -1) this.failedSrcs.splice(known, 1);
  }

  removeBrokenImages() {
    const el = this.$refs.editor as HTMLElement;
    el.innerHTML = removeBrokenImages(el.innerHTML, this.failedSrcs);
    this.failedSrcs = [];
    this.onInput();
  }

  @Watch('value')
  onValueChanged(val: string) {
    const el = this.$refs.editor as HTMLElement;
    if (el && el.innerHTML !== val) {
      el.innerHTML = val || '';
    }
  }

  onInput() {
    const el = this.$refs.editor as HTMLElement;
    this.$emit('input', el.innerHTML);
  }

  exec(cmd: string) {
    document.execCommand(cmd, false);
    (this.$refs.editor as HTMLElement).focus();
    this.updateStates();
    this.onInput();
  }

  execBlock(tag: string) {
    document.execCommand('formatBlock', false, tag);
    (this.$refs.editor as HTMLElement).focus();
    this.onInput();
  }

  updateStates() {
    this.saveSelection();
    this.states = {
      bold:      document.queryCommandState('bold'),
      italic:    document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
    };
  }

  onSelectionChanged() {
    this.saveSelection();
    this.updateStates();
  }

  openImagePicker() {
    this.saveSelection();
    const input = this.$refs.imageInput as HTMLInputElement;
    input.value = '';
    input.click();
  }

  onImagePicked(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    files.forEach(file => this.insertImageFile(file));
    input.value = '';
  }

  onPaste(event: ClipboardEvent) {
    const items = event.clipboardData ? Array.from(event.clipboardData.items) : [];
    const html = event.clipboardData ? event.clipboardData.getData('text/html') : '';
    const imageItems = items.filter(item => item.kind === 'file' && item.type.indexOf('image/') === 0);
    if (!imageItems.length) {
      window.setTimeout(() => this.normalizeEditorImages(), 0);
      return;
    }
    if (html) {
      window.setTimeout(() => this.normalizeEditorImages(), 0);
      return;
    }

    event.preventDefault();
    this.saveSelection();
    imageItems.forEach(item => {
      const file = item.getAsFile();
      if (file) this.insertImageFile(file);
    });
  }

  onDrop(event: DragEvent) {
    const files = event.dataTransfer ? Array.from(event.dataTransfer.files) : [];
    this.saveSelection();
    files
      .filter(file => file.type.indexOf('image/') === 0)
      .forEach(file => this.insertImageFile(file));
  }

  private insertImageFile(file: File) {
    if (file.type.indexOf('image/') !== 0) return;

    const reader = new FileReader();
    reader.onload = () => {
      const src = typeof reader.result === 'string' ? reader.result : '';
      if (!src) return;
      this.insertImage(src);
    };
    reader.readAsDataURL(file);
  }

  private insertImage(src: string) {
    this.restoreSelection();
    const html = '<img src="' + this.escapeAttribute(src) + '" alt="" style="max-width:100%;height:auto;display:block;margin:6px 0;">';
    document.execCommand('insertHTML', false, html);
    (this.$refs.editor as HTMLElement).focus();
    this.saveSelection();
    this.onInput();
  }

  private normalizeEditorImages() {
    const el = this.$refs.editor as HTMLElement;
    const images = Array.from(el.querySelectorAll('img'));

    images.forEach((img: HTMLImageElement) => {
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      if (!img.style.display) img.style.display = 'block';
      if (!img.style.margin) img.style.margin = '6px 0';

      const src = img.getAttribute('src') || '';
      if (!src || src.indexOf('data:') === 0 || src.indexOf('cid:') === 0) return;

      this.imageUrlToDataUrl(src)
        .then((dataUrl) => {
          if (!dataUrl) return;
          img.setAttribute('src', dataUrl);
          this.onInput();
        })
        .catch(() => undefined);
    });

    this.onInput();
  }

  private async imageUrlToDataUrl(src: string): Promise<string> {
    const response = await fetch(src, {credentials: 'include'});
    if (!response.ok) return '';

    const type = response.headers.get('content-type') || '';
    if (type.indexOf('image/') !== 0) return '';

    const blob = await response.blob();
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.onerror = () => resolve('');
      reader.readAsDataURL(blob);
    });
  }

  private saveSelection() {
    const el = this.$refs.editor as HTMLElement;
    const selection = window.getSelection();
    if (!el || !selection || !selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) return;
    this.savedRange = range.cloneRange();
  }

  private restoreSelection() {
    const el = this.$refs.editor as HTMLElement;
    el.focus();
    if (!this.savedRange) return;
    const selection = window.getSelection();
    if (!selection) return;
    selection.removeAllRanges();
    selection.addRange(this.savedRange);
  }

  private escapeAttribute(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
